<template>
	<transition-group name="fade" tag="div" class="tag-container">
		<slot name="before-tags"/>
		<t-tag
			v-for="(item, index) in internalItems"
			:key="itemIsObject ? getId(item) : item"
			:type="item.iconType"
			:icon="item.icon"
			:color="item.color"
			:notRemovable="notRemovable || item.notRemovable"
			:value="itemIsObject ? getDisplayValue(item) : item"
			:style="isClickable && 'cursor: pointer;'"
			@remove="onRemoveTag(item)"
			@click="$emit('tag-click', item)"
		>
			<slot :item="item" :index="index">
				<template v-if="itemIsObject">
					{{getDisplayValue(item)}}
				</template>
				<template v-else>
					{{item}}
				</template>
			</slot>
		</t-tag>
	</transition-group>
</template>

<script>
import TTag from './TTag';
import deepCopy from 'deepcopy';
import { objectGet } from '@/core';

export default {
	name: 'TTagContainer',
	components: { TTag },
	props: {
		items: Array,
		displayKey: { type:String,default:'label' },
		idPath: { type: String, default: 'id' },
		notRemovable: Boolean
	},
	data() {
		return { internalItems: Array };
	},
	computed: {
		itemIsObject() {
			if (!this.items && this.items.length) return false;
			return typeof (this.items[0]) === 'object' && this.items[0] !== null;
		},
		isClickable() {
			return this.$listeners && typeof this.$listeners['tag-click'] === 'function';
		},
	},
	watch: {
		items(newVal) {
			if (newVal === this.internalItems) return;
			this.internalItems = deepCopy(newVal);
		}
	},
	mounted() {
		this.internalItems = deepCopy(this.items);
	},
	methods: {
		onRemoveTag(item) {
			let index = this.internalItems.indexOf(item);
			this.internalItems.splice(index, 1);
			this.$emit('change', this.internalItems);
		},
		getDisplayValue(item) {
			return objectGet(item, this.displayKey);
		},
		getId(item) {
			return objectGet(item, this.idPath);
		},
	}
};
</script>

<style lang="scss" scoped>
	.tag-container {
		display: grid;
		grid-column-gap: 8px;
		grid-row-gap: 8px;
		grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
	}

	.fade-enter-active {
		transition: all .2s ease-in;
	}

	.fade-leave-active {
		position: absolute;
		transition: all .2s ease-out;
	}

	.fade-enter, .fade-leave-to {
		opacity: 0;
		transform: scale(.95);
	}

	.fade-move {
		transition: transform .4s ease-in-out;
	}
</style>

<docs>
## Examples

### Default

```jsx noeditor
let tags = [
	{ id: 1, label: 'Ålesund (6000)', iconType: 'bp', icon: 'map-marker' },
	{ id: 2, label: 'Hareidlandet', iconType: 'bp', icon: 'polygon-filter' },
	{ id: 3, label: 'Gurskøy', iconType: 'bp', icon: 'polygon-filter' },
	{ id: 4, label: 'Utenfor Ålesundregionen', iconType: 'bp', icon: 'polygon-filter', color:'red' }
];

new Vue({
	data(){
		return {
			blueTags: tags,
			labels: [ 'Ålesund', 'Åndalsnes', 'Oslo', 'Trondheim', 'Veøy', 'Bergen', 'Tromsø', 'Bodø', 'Stavanger' ],
			labels2: [ '(6000)', '(6300)', '(0015)', '(7030)', '(6100)', '(5020)', '(9020)', '(8090)', '(4040)' ],
			icons: ['polygon-filter', 'map-marker'],
			colors: ['red', 'yellow', 'blue'],

		}
	},
	methods: {
		addItem() {
			let newTag = {
				label: this.getRandomLabel(),
				icon: this.getRandomIcon(),
				color: this.getRandomColor(),
				iconType: 'bp',
				id: Math.floor(Math.random() * Math.floor(10000) + 5)
			}
			this.blueTags.push(newTag);
		},
		getRandomLabel() {
			return `${this.labels[Math.floor(Math.random() * this.labels.length)]} ${this.labels2[Math.floor(Math.random() * this.labels2.length)]}`;
		},

		getRandomIcon() {
			return this.icons[Math.floor(Math.random() * this.icons.length)];
		},

		getRandomColor() {
			return this.colors[Math.floor(Math.random() * this.colors.length)];
		}
	},
	template: `
		<div>
			<t-button @click="addItem" iconBefore="plus" appearance="minimal" style="margin-bottom: 10px;">Add item</t-button>
			<t-tag-container :items="blueTags">
			</t-tag-container>
		</div>
	`
});
```

#### Example Code
```html
...js
blueTags: [
	{ id: 1, label: 'Ålesund (6000)', iconType: 'bp', icon: 'map-marker' },
	{ id: 2, label: 'Hareidlandet', iconType: 'bp', icon: 'polygon-filter' },
	{ id: 3, label: 'Gurskøy', iconType: 'bp', icon: 'polygon-filter' },
	{ id: 4, label: 'Utenfor Ålesundregionen', iconType: 'bp', icon: 'polygon-filter', color:'red' }
];
...

<t-tag-container :items="blueTags">
</t-tag-container>
```
### Without Icons

```jsx noeditor
<t-tag-container :items="[{id: 1, label: 'Ålesund (6000)' }, { id: 2, label: 'Åndalsnes' }]" displayKey="label">
</t-tag-container>
```
#### Example Code
```html
<t-tag-container :items="[{id: 1, label: 'Ålesund (6000)' }, { id: 2, label: 'Åndalsnes' }]" displayKey="label">
</t-tag-container>
```

</docs>
